/**
 * Regenerating an existing qualification with the AI: which fields the
 * suggestion changes compared with what the admin has in the form.
 * Nothing here writes: the form only sends its values on "Confirmer".
 */

import { normalizeTaskLabel } from 'components/SlideOver/dataSlideOver/data/slideOverAddMission/missionTemplate/templateFormData';
import { mergeSuggestedTasks, toComparableText } from './qualificationAiSuggestion';
import {
  isConditionsValue,
  requirementNameKey,
  toFormItems,
  toFormRisk,
  toSuggestedItems,
  toSuggestedRisk,
} from './qualificationRequirements';
import { MAX_ACTIVE_TASKS, isTaskUsed, toFormTasks } from './qualificationTasks';

// The name is never regenerated: it is locked once the qualification exists.
export const REGENERATED_FIELDS = [
  'description',
  'rome',
  'pcs',
  'sector',
  'tasks',
  'conditions',
  'tools',
  'licences',
];

const toCode = (value) => String(value ?? '').trim().toUpperCase();

/** The regenerated fields as loaded from the database, in the form's shapes. */
export const toStoredValues = (qualification) => ({
  description: qualification?.desc ?? '',
  rome: { code: qualification?.codeROME ?? '', label: '' },
  pcs: { code: qualification?.codePCS ?? '', label: '' },
  sector: qualification?.type ?? '',
  tasks: toFormTasks(qualification?.tasks),
  conditions: toFormRisk(qualification?.conditions),
  tools: toFormItems(qualification?.tools),
  licences: toFormItems(qualification?.licences),
});

/**
 * Same merge as the creation, applied to the form's tasks, so a task the
 * admin added by hand stays. A saved AI task used by a job description stays
 * too: removing it by hand asks for a confirmation the regeneration would
 * otherwise skip, and saving would archive it. A task the AI proposes again
 * keeps its id, even one removed from the form since the load (`knownTasks`),
 * so saving does not archive and recreate it.
 */
export const mergeRegeneratedTasks = (
  currentTasks,
  suggestedTasks,
  knownTasks = []
) => {
  const knownByLabel = new Map(
    [...knownTasks, ...currentTasks].map((task) => [
      normalizeTaskLabel(task.label),
      task,
    ])
  );
  const keptTasks = currentTasks.filter(
    (task) => task.source !== 'ai' || isTaskUsed(task)
  );
  const keptKeys = new Set(
    keptTasks.map((task) => normalizeTaskLabel(task.label))
  );
  const newAiTasks = mergeSuggestedTasks([], suggestedTasks)
    .filter((task) => !keptKeys.has(normalizeTaskLabel(task.label)))
    .slice(0, Math.max(0, MAX_ACTIVE_TASKS - keptTasks.length))
    .map((task) => knownByLabel.get(normalizeTaskLabel(task.label)) ?? task);
  return [...newAiTasks, ...keptTasks];
};

/**
 * The values a `done` suggestion proposes. A field the answer leaves empty or
 * omits is not proposed: it must not wipe the admin's value.
 */
export const toRegeneratedValues = (
  suggestion,
  baseline,
  sectorKeys,
  knownTasks = []
) => {
  const values = {};
  if (toComparableText(suggestion?.descriptionHtml)) {
    values.description = suggestion.descriptionHtml;
  }
  ['rome', 'pcs'].forEach((field) => {
    const code = toCode(suggestion?.[field]?.code);
    if (code) values[field] = { code, label: suggestion[field].label ?? '' };
  });
  if (sectorKeys.includes(suggestion?.sector?.type)) {
    values.sector = suggestion.sector.type;
  }
  if (Array.isArray(suggestion?.tasks)) {
    values.tasks = mergeRegeneratedTasks(
      baseline.tasks,
      suggestion.tasks,
      knownTasks
    );
  }
  if (isConditionsValue(suggestion?.conditions)) {
    values.conditions = toSuggestedRisk(suggestion.conditions);
  }
  ['tools', 'licences'].forEach((field) => {
    if (Array.isArray(suggestion?.[field])) {
      values[field] = toSuggestedItems(suggestion[field]);
    }
  });
  return values;
};

const isSameKeySet = (left, right) => {
  const leftKeys = new Set(left);
  const rightKeys = new Set(right);
  return (
    leftKeys.size === rightKeys.size &&
    [...leftKeys].every((key) => rightKeys.has(key))
  );
};

const checkedFactors = (risk) =>
  Object.keys(risk.penibilite).filter((field) => risk.penibilite[field]);

/** Whether two values of a field read the same to the admin. */
export const isSameFieldValue = (field, left, right) => {
  switch (field) {
    case 'description':
      return toComparableText(left) === toComparableText(right);
    case 'rome':
    case 'pcs':
      return toCode(left.code) === toCode(right.code);
    case 'sector':
      return left === right;
    case 'tasks':
      return isSameKeySet(
        left.map((task) => normalizeTaskLabel(task.label)),
        right.map((task) => normalizeTaskLabel(task.label))
      );
    case 'conditions':
      return (
        left.positionAtRisk === right.positionAtRisk &&
        isSameKeySet(checkedFactors(left), checkedFactors(right))
      );
    case 'tools':
    case 'licences':
      return isSameKeySet(
        left.map((item) => requirementNameKey(item.name)),
        right.map((item) => requirementNameKey(item.name))
      );
    default:
      return false;
  }
};

/** The code with its label when the suggestion happens to name it. */
const withKnownLabel = (field, baselineCode, suggestion) => {
  const candidates = [
    suggestion?.[field],
    ...(suggestion?.alternatives?.[field] ?? []),
  ];
  const known = candidates.find(
    (candidate) =>
      candidate?.code && toCode(candidate.code) === toCode(baselineCode.code)
  );
  return { ...baselineCode, label: known?.label || baselineCode.label };
};

/**
 * What a regeneration compares each field with: the form value, except for a
 * field still showing a previous suggestion, which is compared with the value
 * that suggestion replaced. So a second regeneration never measures itself
 * against the first one, and the admin's own edits are what « Avant » keeps.
 */
export const toRegenerationBaseline = (current, previousChanges) =>
  REGENERATED_FIELDS.reduce((baseline, field) => {
    const change = previousChanges[field];
    const showsSuggestion =
      change &&
      !change.isRestored &&
      isSameFieldValue(field, current[field], change.after);
    return {
      ...baseline,
      [field]: showsSuggestion ? change.before : current[field],
    };
  }, {});

/**
 * `proposed`: the values of the fields the suggestion carries.
 * `changes`: the proposed fields that differ from the baseline, each with
 * the baseline value (`before`) and the suggested one (`after`).
 */
export const diffRegeneration = (
  baseline,
  suggestion,
  sectorKeys,
  knownTasks = []
) => {
  const proposed = toRegeneratedValues(
    suggestion,
    baseline,
    sectorKeys,
    knownTasks
  );
  const changes = REGENERATED_FIELDS.reduce((found, field) => {
    if (!(field in proposed)) return found;
    if (isSameFieldValue(field, proposed[field], baseline[field])) return found;
    const before =
      field === 'rome' || field === 'pcs'
        ? withKnownLabel(field, baseline[field], suggestion)
        : baseline[field];
    return {
      ...found,
      [field]: { before, after: proposed[field], isRestored: false },
    };
  }, {});
  return { proposed, changes };
};
